import React, { useState, useRef, useEffect } from 'react';
import { Tag, Input, Progress, Badge } from 'antd';
import { SearchOutlined, BookOutlined, MenuOutlined, CloseOutlined, CheckCircleFilled, LockOutlined } from '@ant-design/icons';

// ─── Role definitions ─────────────────────────────────────────────────────────
const ROLES = {
    user: { label: 'General User', icon: '👤', color: '#1890ff', desc: 'Assessors, Approvers, Analysts' },
    admin: { label: 'Administrator', icon: '⚙️', color: '#722ed1', desc: 'System Admins, QoC Managers' },
};

// ─── UI Mockup SVG Components ─────────────────────────────────────────────────
const LoginMockup = () => (
    <svg viewBox="0 0 480 280" style={{ width: '100%', maxWidth: 480, borderRadius: 12, boxShadow: '0 4px 24px rgba(0,0,0,0.10)', display: 'block', margin: '18px auto 8px' }}>
        <rect width="480" height="280" fill="#f4f6fb" rx="12"/>
        {/* Top bar */}
        <rect width="480" height="48" fill="#0f172a" rx="0"/>
        <rect y="0" width="480" height="48" fill="#0f172a" rx="12"/>
        <rect y="36" width="480" height="12" fill="#0f172a"/>
        <circle cx="28" cy="24" r="12" fill="#1890ff" opacity="0.7"/>
        <text x="48" y="29" fontFamily="Arial" fontSize="13" fill="#fff" fontWeight="700">QoC Platform</text>
        <text x="340" y="29" fontFamily="Arial" fontSize="11" fill="rgba(255,255,255,0.5)">Eswatini MoH · NQMP</text>
        {/* Login card */}
        <rect x="140" y="68" width="200" height="192" fill="#fff" rx="10" filter="url(#shadow)"/>
        <defs><filter id="shadow"><feDropShadow dx="0" dy="2" stdDeviation="6" floodOpacity="0.08"/></filter></defs>
        <text x="240" y="96" fontFamily="Arial" fontSize="14" fill="#0f172a" fontWeight="800" textAnchor="middle">Sign In</text>
        <text x="240" y="112" fontFamily="Arial" fontSize="10" fill="#94a3b8" textAnchor="middle">Enter your credentials to continue</text>
        {/* Username */}
        <rect x="160" y="122" width="160" height="28" fill="#f8faff" rx="6" stroke="#e2e8f0" strokeWidth="1"/>
        <text x="170" y="140" fontFamily="Arial" fontSize="10" fill="#94a3b8">Username</text>
        {/* Password */}
        <rect x="160" y="158" width="160" height="28" fill="#f8faff" rx="6" stroke="#e2e8f0" strokeWidth="1"/>
        <text x="170" y="176" fontFamily="Arial" fontSize="10" fill="#94a3b8">Password</text>
        {/* Button */}
        <rect x="160" y="196" width="160" height="30" fill="#1890ff" rx="6"/>
        <text x="240" y="215" fontFamily="Arial" fontSize="12" fill="#fff" fontWeight="700" textAnchor="middle">Login</text>
        {/* Clauses at bottom */}
        <text x="240" y="252" fontFamily="Arial" fontSize="9" fill="#94a3b8" textAnchor="middle">AACC · COP · MOM · PRPE · IPCM · PSQI · GROM · FMS · HRE · ISM</text>
    </svg>
);

const DashboardMockup = () => (
    <svg viewBox="0 0 520 300" style={{ width: '100%', maxWidth: 520, borderRadius: 12, boxShadow: '0 4px 24px rgba(0,0,0,0.10)', display: 'block', margin: '18px auto 8px' }}>
        <rect width="520" height="300" fill="#f4f6fb" rx="12"/>
        {/* Sidebar */}
        <rect width="110" height="300" fill="#fff" rx="0"/>
        <rect width="110" height="300" fill="#fff" rx="12"/>
        <rect x="98" width="12" height="300" fill="#fff"/>
        <rect x="0" y="0" width="110" height="44" fill="#0f172a" rx="12"/>
        <rect x="0" y="32" width="110" height="12" fill="#0f172a"/>
        <circle cx="22" cy="22" r="8" fill="#1890ff" opacity="0.7"/>
        <text x="34" y="26" fontFamily="Arial" fontSize="9" fill="#fff" fontWeight="700">QoC Platform</text>
        {/* Sidebar items */}
        {[['📋 Assessments', 55], ['✅ Approval', 73], ['📊 Analytics', 91], ['📄 Reports', 109], ['⚙️ Settings', 127]].map(([label, y], i) => (
            <g key={i}>
                <rect x="6" y={y - 10} width="98" height="20" fill={i === 2 ? '#e6f4ff' : 'transparent'} rx="4"/>
                <text x="14" y={y + 4} fontFamily="Arial" fontSize="9" fill={i === 2 ? '#1890ff' : '#64748b'} fontWeight={i === 2 ? '700' : '400'}>{label}</text>
            </g>
        ))}
        {/* Main content */}
        <text x="130" y="30" fontFamily="Arial" fontSize="13" fill="#0f172a" fontWeight="800">Executive Dashboard</text>
        {/* KPI cards */}
        {[['Avg Score', '87.3%', '#1890ff'], ['Assessed', '142', '#52c41a'], ['High Perf', '38', '#389e0d'], ['Attention', '12', '#f5222d']].map(([label, val, color], i) => (
            <g key={i}>
                <rect x={130 + i * 96} y="44" width="88" height="52" fill="#fff" rx="8" stroke={color} strokeWidth="1" strokeOpacity="0.3"/>
                <rect x={130 + i * 96} y="44" width="88" height="4" fill={color} rx="2"/>
                <text x={174 + i * 96} y="72" fontFamily="Arial" fontSize="14" fill={color} fontWeight="900" textAnchor="middle">{val}</text>
                <text x={174 + i * 96} y="86" fontFamily="Arial" fontSize="8" fill="#94a3b8" textAnchor="middle">{label}</text>
            </g>
        ))}
        {/* Chart area */}
        <rect x="130" y="108" width="250" height="120" fill="#fff" rx="8"/>
        <text x="144" y="124" fontFamily="Arial" fontSize="9" fill="#0f172a" fontWeight="700">National Compliance Trend</text>
        {/* Trend line */}
        <polyline points="144,210 180,200 216,195 252,185 288,188 324,178 360,172" fill="none" stroke="#1890ff" strokeWidth="2"/>
        <polyline points="144,210 180,200 216,195 252,185 288,188 324,178 360,172 360,220 144,220" fill="#1890ff" fillOpacity="0.08"/>
        <line x1="144" y1="188" x2="360" y2="188" stroke="#52c41a" strokeDasharray="4,3" strokeWidth="1"/>
        <text x="362" y="191" fontFamily="Arial" fontSize="7" fill="#52c41a">90%</text>
        {/* Right panel */}
        <rect x="390" y="108" width="120" height="120" fill="#fff" rx="8"/>
        <text x="404" y="124" fontFamily="Arial" fontSize="9" fill="#0f172a" fontWeight="700">Score Distribution</text>
        <circle cx="450" cy="172" r="34" fill="none" stroke="#e8e8e8" strokeWidth="16"/>
        <circle cx="450" cy="172" r="34" fill="none" stroke="#52c41a" strokeWidth="16" strokeDasharray="85 130" strokeDashoffset="-32"/>
        <circle cx="450" cy="172" r="34" fill="none" stroke="#1890ff" strokeWidth="16" strokeDasharray="45 130" strokeDashoffset="-117"/>
        <text x="450" y="176" fontFamily="Arial" fontSize="9" fill="#0f172a" fontWeight="700" textAnchor="middle">142</text>
        {/* Facility list */}
        <rect x="130" y="238" width="380" height="52" fill="#fff" rx="8"/>
        <text x="144" y="254" fontFamily="Arial" fontSize="9" fill="#0f172a" fontWeight="700">Top Facilities</text>
        {[['🥇 Mbabane Gov. Hospital', '97.2%', '#d4af37'], ['🥈 Mankayane Hospital', '94.8%', '#a8a8a8'], ['🥉 Pigg\'s Peak Hospital', '92.1%', '#b08d57']].map(([name, score, color], i) => (
            <g key={i}>
                <text x="144" y={266 + i * 11} fontFamily="Arial" fontSize="8" fill="#334155">{name}</text>
                <text x="492" y={266 + i * 11} fontFamily="Arial" fontSize="8" fill={color} fontWeight="700" textAnchor="end">{score}</text>
            </g>
        ))}
    </svg>
);

const AssessmentMockup = () => (
    <svg viewBox="0 0 520 300" style={{ width: '100%', maxWidth: 520, borderRadius: 12, boxShadow: '0 4px 24px rgba(0,0,0,0.10)', display: 'block', margin: '18px auto 8px' }}>
        <rect width="520" height="300" fill="#f4f6fb" rx="12"/>
        <rect width="520" height="44" fill="#0f172a" rx="12"/>
        <rect y="32" width="520" height="12" fill="#0f172a"/>
        <text x="20" y="28" fontFamily="Arial" fontSize="13" fill="#fff" fontWeight="800">Assessment — AACC · Mbabane Government Hospital · FY2025/26</text>
        <rect x="420" y="10" width="80" height="24" fill="#52c41a" rx="6"/>
        <text x="460" y="26" fontFamily="Arial" fontSize="10" fill="#fff" fontWeight="700" textAnchor="middle">Submit ✓</text>
        {/* Score bar */}
        <rect x="20" y="56" width="480" height="32" fill="#fff" rx="8"/>
        <text x="32" y="76" fontFamily="Arial" fontSize="11" fill="#0f172a" fontWeight="700">Clause Score:</text>
        <text x="130" y="76" fontFamily="Arial" fontSize="14" fill="#52c41a" fontWeight="900">87.3%</text>
        <rect x="220" y="64" width="200" height="16" fill="#f0f0f0" rx="8"/>
        <rect x="220" y="64" width="175" height="16" fill="#52c41a" rx="8"/>
        <text x="432" y="76" fontFamily="Arial" fontSize="10" fill="#94a3b8">★★★ Good</text>
        {/* ME rows */}
        {[
            ['AACC.1.1', 'Admission procedures are documented and communicated', 'Met', '#52c41a'],
            ['AACC.1.2', 'Patient assessment completed within defined timeframe', 'Met', '#52c41a'],
            ['AACC.1.3 ⚠ KCS', 'Emergency protocols are posted and accessible', 'Partially Met', '#fa8c16'],
            ['AACC.1.4', 'Referral pathways are documented', 'Not Met', '#f5222d'],
            ['AACC.1.5', 'Discharge planning initiated on admission', 'Met', '#52c41a'],
        ].map(([code, text, status, color], i) => (
            <g key={i}>
                <rect x="20" y={96 + i * 38} width="480" height="34" fill="#fff" rx="6" stroke="#f0f0f0" strokeWidth="1"/>
                <text x="32" y={110 + i * 38} fontFamily="Arial" fontSize="9" fill="#1890ff" fontWeight="700">{code}</text>
                <text x="32" y={123 + i * 38} fontFamily="Arial" fontSize="9" fill="#334155">{text}</text>
                <rect x="390" y={100 + i * 38} width="80" height="20" fill={color} fillOpacity="0.12" rx="5" stroke={color} strokeWidth="1" strokeOpacity="0.4"/>
                <text x="430" y={114 + i * 38} fontFamily="Arial" fontSize="8" fill={color} fontWeight="700" textAnchor="middle">{status}</text>
            </g>
        ))}
    </svg>
);

const ApprovalMockup = () => (
    <svg viewBox="0 0 520 260" style={{ width: '100%', maxWidth: 520, borderRadius: 12, boxShadow: '0 4px 24px rgba(0,0,0,0.10)', display: 'block', margin: '18px auto 8px' }}>
        <rect width="520" height="260" fill="#f4f6fb" rx="12"/>
        <rect width="520" height="44" fill="#0f172a" rx="12"/>
        <rect y="32" width="520" height="12" fill="#0f172a"/>
        <text x="20" y="28" fontFamily="Arial" fontSize="13" fill="#fff" fontWeight="800">Approval Queue</text>
        <rect x="400" y="10" width="100" height="24" fill="#1890ff" rx="6"/>
        <text x="450" y="26" fontFamily="Arial" fontSize="10" fill="#fff" fontWeight="700" textAnchor="middle">Bulk Approve</text>
        {/* Filter row */}
        <rect x="20" y="54" width="480" height="28" fill="#fff" rx="6"/>
        {['All Facilities', 'All Clauses', 'Submitted', 'This Period'].map((f, i) => (
            <g key={i}>
                <rect x={28 + i * 118} y="58" width="110" height="18" fill="#f8faff" rx="4" stroke="#e2e8f0" strokeWidth="1"/>
                <text x={83 + i * 118} y="70" fontFamily="Arial" fontSize="9" fill="#64748b" textAnchor="middle">{f}</text>
            </g>
        ))}
        {/* Table header */}
        <rect x="20" y="90" width="480" height="22" fill="#f0f5ff" rx="4"/>
        {['', 'Facility', 'Clause', 'Date', 'Score', 'Status', 'Action'].map((h, i) => (
            <text key={i} x={[30, 50, 200, 260, 320, 370, 440][i]} y="105" fontFamily="Arial" fontSize="9" fill="#1a1a2e" fontWeight="700">{h}</text>
        ))}
        {/* Rows */}
        {[
            ['Mbabane Gov. Hospital', 'AACC', '12 Jul 26', '87.3%', 'Submitted'],
            ['Mankayane Hospital', 'COP', '11 Jul 26', '92.1%', 'Submitted'],
            ['Pigg\'s Peak Hospital', 'IPCM', '10 Jul 26', '74.8%', 'Submitted'],
            ['Hlatikhulu Hospital', 'FMS', '09 Jul 26', '95.6%', 'Submitted'],
        ].map(([fac, clause, date, score, status], i) => (
            <g key={i}>
                <rect x="20" y={116 + i * 32} width="480" height="28" fill={i % 2 ? '#fafbff' : '#fff'} rx="4"/>
                <rect x="24" y={122 + i * 32} width="16" height="16" fill="#f0f0f0" rx="3" stroke="#e2e8f0" strokeWidth="1"/>
                <text x="50" y={134 + i * 32} fontFamily="Arial" fontSize="9" fill="#334155">{fac}</text>
                <rect x="196" y={121 + i * 32} width="36" height="16" fill="#e6f4ff" rx="4"/>
                <text x="214" y={133 + i * 32} fontFamily="Arial" fontSize="8" fill="#1890ff" fontWeight="700" textAnchor="middle">{clause}</text>
                <text x="260" y={134 + i * 32} fontFamily="Arial" fontSize="9" fill="#94a3b8">{date}</text>
                <text x="320" y={134 + i * 32} fontFamily="Arial" fontSize="9" fill="#52c41a" fontWeight="700">{score}</text>
                <rect x="362" y={121 + i * 32} width="60" height="16" fill="#fffbe6" rx="4"/>
                <text x="392" y={133 + i * 32} fontFamily="Arial" fontSize="8" fill="#fa8c16" fontWeight="700" textAnchor="middle">{status}</text>
                <rect x="430" y={121 + i * 32} width="52" height="16" fill="#1890ff" rx="4"/>
                <text x="456" y={133 + i * 32} fontFamily="Arial" fontSize="8" fill="#fff" fontWeight="700" textAnchor="middle">Review →</text>
            </g>
        ))}
    </svg>
);

const SettingsMockup = () => (
    <svg viewBox="0 0 520 260" style={{ width: '100%', maxWidth: 520, borderRadius: 12, boxShadow: '0 4px 24px rgba(0,0,0,0.10)', display: 'block', margin: '18px auto 8px' }}>
        <rect width="520" height="260" fill="#f4f6fb" rx="12"/>
        <rect width="520" height="44" fill="#0f172a" rx="12"/>
        <rect y="32" width="520" height="12" fill="#0f172a"/>
        <text x="20" y="28" fontFamily="Arial" fontSize="13" fill="#fff" fontWeight="800">Access Control · Role Permissions</text>
        {/* Tabs */}
        <rect x="20" y="54" width="150" height="24" fill="#fff" rx="6" stroke="#1890ff" strokeWidth="1.5"/>
        <text x="95" y="70" fontFamily="Arial" fontSize="10" fill="#1890ff" fontWeight="700" textAnchor="middle">Role Permissions</text>
        <rect x="178" y="54" width="150" height="24" fill="#f8faff" rx="6" stroke="#e2e8f0" strokeWidth="1"/>
        <text x="253" y="70" fontFamily="Arial" fontSize="10" fill="#94a3b8" textAnchor="middle">Module Management</text>
        {/* Role cards */}
        {[['Facility Assessor', '3 modules', '#52c41a'], ['Approval Officer', '2 modules', '#fa8c16'], ['National Analyst', '4 modules', '#722ed1'], ['QoC Administrator', '8 modules', '#1890ff']].map(([role, modules, color], i) => (
            <g key={i}>
                <rect x={20 + (i % 2) * 250} y={88 + Math.floor(i / 2) * 80} width="232" height="68" fill="#fff" rx="8" stroke={color} strokeWidth="1" strokeOpacity="0.3"/>
                <rect x={20 + (i % 2) * 250} y={88 + Math.floor(i / 2) * 80} width="232" height="4" fill={color} rx="2"/>
                <text x={36 + (i % 2) * 250} y={110 + Math.floor(i / 2) * 80} fontFamily="Arial" fontSize="11" fill="#0f172a" fontWeight="700">{role}</text>
                <rect x={36 + (i % 2) * 250} y={118 + Math.floor(i / 2) * 80} width="60" height="16" fill={color} fillOpacity="0.1" rx="4"/>
                <text x={66 + (i % 2) * 250} y={130 + Math.floor(i / 2) * 80} fontFamily="Arial" fontSize="9" fill={color} fontWeight="700" textAnchor="middle">{modules}</text>
                <rect x={180 + (i % 2) * 250} y={118 + Math.floor(i / 2) * 80} width="56" height="20" fill={color} rx="6"/>
                <text x={208 + (i % 2) * 250} y={132 + Math.floor(i / 2) * 80} fontFamily="Arial" fontSize="9" fill="#fff" fontWeight="700" textAnchor="middle">Edit →</text>
            </g>
        ))}
    </svg>
);

const SurveyMockup = () => (
    <svg viewBox="0 0 400 320" style={{ width: '100%', maxWidth: 400, borderRadius: 12, boxShadow: '0 4px 24px rgba(0,0,0,0.10)', display: 'block', margin: '18px auto 8px' }}>
        <rect width="400" height="320" fill="#f4f6fb" rx="12"/>
        <rect width="400" height="52" fill="#006837" rx="12"/>
        <rect y="40" width="400" height="12" fill="#006837"/>
        <text x="200" y="22" fontFamily="Arial" fontSize="12" fill="#fff" fontWeight="800" textAnchor="middle">CUSTOMER SATISFACTION SURVEY</text>
        <text x="200" y="38" fontFamily="Arial" fontSize="9" fill="rgba(255,255,255,0.7)" textAnchor="middle">Ministry of Health · NQMP · Please tick your answer</text>
        {/* Progress */}
        <rect x="20" y="62" width="360" height="8" fill="#e8f5e9" rx="4"/>
        <rect x="20" y="62" width="216" height="8" fill="#006837" rx="4"/>
        <text x="200" y="84" fontFamily="Arial" fontSize="9" fill="#006837" fontWeight="700" textAnchor="middle">60% complete</text>
        {/* Questions */}
        {[
            ['Q1', 'How long did you wait before receiving service?', ['< 30 Min ✓', '30-60 Min', '1-2 Hours', '> 2 Hours'], 0],
            ['Q2', 'How was the attitude of the health care provider?', ['Very Good ✓', 'Good', 'Average', 'Poor'], 0],
            ['Q3', 'How satisfied were you with the service received?', ['Very Satisfied', 'Satisfied ✓', 'Unsatisfied', 'Very Unsatisfied'], 1],
        ].map(([ref, text, opts, selected], i) => (
            <g key={i}>
                <rect x="20" y={96 + i * 68} width="360" height="60" fill="#fff" rx="8" stroke="#e2e8f0" strokeWidth="1"/>
                <rect x="20" y={96 + i * 68} width="3" height="60" fill="#006837" rx="2"/>
                <text x="32" y={113 + i * 68} fontFamily="Arial" fontSize="9" fill="#006837" fontWeight="700">{ref}</text>
                <text x="54" y={113 + i * 68} fontFamily="Arial" fontSize="9" fill="#334155">{text}</text>
                {opts.map((opt, j) => (
                    <g key={j}>
                        <rect x={32 + j * 86} y={121 + i * 68} width="80" height="22" fill={j === selected ? '#e8f5e9' : '#f8faff'} rx="5" stroke={j === selected ? '#006837' : '#e2e8f0'} strokeWidth={j === selected ? 1.5 : 1}/>
                        <text x={72 + j * 86} y={136 + i * 68} fontFamily="Arial" fontSize="8" fill={j === selected ? '#006837' : '#64748b'} fontWeight={j === selected ? '700' : '400'} textAnchor="middle">{opt}</text>
                    </g>
                ))}
            </g>
        ))}
    </svg>
);

// ─── Manual content ───────────────────────────────────────────────────────────
const SECTIONS_USER = [
    {
        id: 'getting-started',
        icon: '🚀',
        title: 'Getting Started',
        color: '#1890ff',
        roles: ['user', 'admin'],
        topics: [
            {
                id: 'gs-overview',
                title: 'What is the QoC Platform?',
                readTime: 3,
                graphic: <LoginMockup />,
                content: `
The Eswatini Quality of Care (QoC) Platform is the Ministry of Health's digital tool for managing, tracking, and reporting on health facility quality assessments across the country.

**What it does:**
- Manages structured audits against 10 QoC standard clauses
- Tracks compliance scores from audit through to approval
- Provides national-level analytics dashboards
- Collects inpatient and outpatient patient satisfaction surveys
- Generates detailed audit reports (PDF and Excel)
- Supports bulk import from KoboCollect and Excel templates

**The 10 QoC Clauses:**

| # | Code | Title |
|---|---|---|
| 1 | AACC | Access, Assessment and Continuity of Care |
| 2 | COP | Care of the Patient |
| 3 | MOM | Management of Medication |
| 4 | PRPE | Patient Rights and Patient Education |
| 5 | IPCM | Infection Prevention and Control Management |
| 6 | PSQI | Patient Safety and Quality Improvement |
| 7 | GROM | Governance, Roles and Responsibilities of Management |
| 8 | FMS | Facility Management, Safety and Support Services |
| 9 | HRE | Human Resource and Education |
| 10 | ISM | Information System Management |
                `
            },
            {
                id: 'gs-login',
                title: 'Logging In',
                readTime: 2,
                content: `
Navigate to the platform URL provided by your administrator. Enter your **username** and **password** and click Login.

The login page displays the 10 QoC clauses — these are fetched live from the system so any new standards added by your administrator appear here automatically.

**Your permissions control what you see.** The navigation menu shows only the sections your role has been granted access to. If you expect to see a section that isn't showing, contact your administrator to check your role permissions.

**Common permission roles:**

| Permission | What you can do |
|---|---|
| \`qoc_assess\` | Create and submit assessments, import from KoboCollect |
| \`qoc_approve\` | Review, approve, reject, or request revision on assessments |
| \`qoc_view\` | View dashboards and analytics (read-only) |
| \`qoc_admin\` | Full access — standards setup, periods, settings, all reports |

**Forgot your password?** Contact your system administrator. There is no self-service password reset.
                `
            },
            {
                id: 'gs-navigation',
                title: 'Getting Around',
                readTime: 2,
                content: `
The platform uses a **left sidebar** for navigation, organised into sections. Click any menu item to navigate to that page.

**Administration**
- Roles & Users — Manage accounts and roles
- Facilities — Manage the master facility list

**Quality of Care**
- Standards Setup — Clause and ME hierarchy
- Approval Queue — Review submitted assessments
- Assessments — Create and manage audits
- Import (KoBo) — Bulk import from KoboCollect or Excel
- Corrective Actions — CAP item tracking

**National Oversight**
- Analytics — Facility compliance overview
- Compliance — Detailed compliance tracking
- Customer Surveys — Patient satisfaction data
- Quality Index — Composite QoC + survey score

**Reports**
- Executive Dashboard — Full national performance view
- Facility Report — Per-facility PDF/Excel export
- Custom Dashboards — Build bespoke views

**Settings** *(admin only)*
- Assessment Periods — Define audit cycles
- Audit Schedules — Plan and track audits
- Access Control — Manage role permissions
- HIE Facility Sync — Sync from national HIE
                `
            },
        ]
    },
    {
        id: 'assessments',
        icon: '📋',
        title: 'Assessments',
        color: '#52c41a',
        roles: ['user', 'admin'],
        topics: [
            {
                id: 'as-create',
                title: 'Creating an Assessment',
                readTime: 4,
                graphic: <AssessmentMockup />,
                content: `
1. Go to **Quality of Care → Assessments**
2. Click **New Assessment**
3. Select the **Facility** and **Audit Period**
4. Select the **Clause** being assessed (e.g. AACC, COP)
5. Set the **Audit Date** and assign the **Lead Assessor**
6. Click **Save** to create a draft

Each assessment covers **one clause**. A full facility audit with all 10 clauses creates 10 separate assessment records, which are grouped together in reports and dashboards by period.

**Assessment statuses:**

| Status | Meaning |
|---|---|
| Draft | Created but not yet submitted |
| Submitted | Submitted for review — visible in dashboards |
| Approved | Approved — final status, score locked |
| Rejected | Returned — requires correction before resubmission |
| Revision Requested | Approver flagged specific items for correction |

**Important:** Only Submitted and Approved assessments appear in dashboards and analytics. Drafts are excluded.
                `
            },
            {
                id: 'as-responses',
                title: 'Recording Measurable Elements',
                readTime: 5,
                content: `
Once an assessment is open, fill in responses for each **Measurable Element (ME)** — the specific questions within the clause.

**Fulfillment options:**

| Option | Score | When to use |
|---|---|---|
| Met | 100% | Requirement is fully satisfied |
| Partially Met | 50% | Requirement is partially satisfied |
| Not Met | 0% | Requirement is not satisfied |
| Not Applicable | Excluded | Does not apply to this facility type |
| Not Checked | Excluded | Could not be assessed during this audit |

**Scoring formula:**
- A = Total audited MEs (Met + Partially Met + Not Met only — NA and NC are excluded)
- Score per Met ME = 100 ÷ A
- Clause Score = (Met × Score) + (Partially Met × Score ÷ 2)

**⚠ Key Critical Standards (KCS):** MEs marked KCS are critical. Failure on a KCS is flagged immediately and highlighted in red in reports.

For each ME you can also record:
- **Comment** — Required when Not Met or Partially Met
- **General Observation** — Descriptive note
- **Criticality** — High / Medium / Low
- **Area of Good Practice** — Positive findings
                `
            },
            {
                id: 'as-submit',
                title: 'Submitting for Approval',
                readTime: 2,
                content: `
When all MEs are completed:

1. Review the **clause score** shown at the top of the assessment
2. Click **Submit for Approval**
3. The status changes to "Submitted" and the assessment appears in the approver's queue

**After submission:**
- The assessment is visible in dashboards and analytics immediately
- You cannot edit a submitted assessment unless the approver rejects it or requests a revision
- If rejected, you will receive the rejection reason and can edit and resubmit

**Rating scale:**

| Score | Rating | Stars |
|---|---|---|
| 98 – 100% | Outstanding | ★★★★★ |
| 95 – 97.9% | Excellent | ★★★★ |
| 90 – 94.9% | Good | ★★★ |
| 85 – 89.9% | Fair | ★★ |
| 70 – 84.9% | Requires Improvement | ★ |
| 0 – 69.9% | Inadequate | — |
                `
            },
            {
                id: 'as-import',
                title: 'Importing from KoboCollect',
                readTime: 4,
                content: `
If assessments were conducted offline using KoboCollect tablets:

1. Go to **Quality of Care → Import (KoBo)**
2. Select the **Clause** the export covers — this is critical
3. Select the **Facility** and **Period**
4. Upload the exported KoboCollect file (.xlsx or .csv)
5. Review the mapped responses in the preview table
6. Click **Import** to save all records

**Why you must select the correct clause first:**
KoboCollect exports ME codes in short format (e.g. \`1.1\`) but the database stores prefixed codes (e.g. \`AACC.1.1\`). The system automatically applies the clause prefix during import based on your selection.

**Excel template import:**
1. Download the offline template from Assessment Import
2. Fill in fulfillment responses using the provided dropdowns
3. Upload the completed file
4. Review the mapped data and confirm

**Common issue — duplicate entry error:**
The same assessment has already been imported. Check the assessments list before re-importing.
                `
            },
        ]
    },
    {
        id: 'approval',
        icon: '✅',
        title: 'Approval Workflow',
        color: '#fa8c16',
        roles: ['user', 'admin'],
        topics: [
            {
                id: 'ap-review',
                title: 'Reviewing Assessments',
                readTime: 3,
                graphic: <ApprovalMockup />,
                content: `
Users with the \`qoc_approve\` permission access the **Approval Queue** under Quality of Care.

The queue shows all submitted assessments with facility name, clause badge, audit date, score, star rating, and submission timestamp.

Click **Review** to open the full assessment and see all ME responses.

**Actions available:**

| Action | Effect |
|---|---|
| Approve | Marks as approved; score is finalised in analytics |
| Reject | Returns to assessor with a required rejection reason |
| Request Revision | Flags specific MEs for correction with notes |

Filter the queue by status, facility, clause, or period using the filter bar at the top.
                `
            },
            {
                id: 'ap-bulk',
                title: 'Bulk Approval',
                readTime: 1,
                content: `
Use the checkboxes on the approval list to select multiple assessments, then click **Bulk Approve** to approve all selected records at once.

This is useful when a facility has submitted all 10 clause assessments on the same date and all are ready for approval.

**Best practice:** Always review at least a sample of the assessments before bulk approving. The review page shows all ME responses, comments, and any KCS failures flagged in red.
                `
            },
        ]
    },
    {
        id: 'dashboards',
        icon: '📊',
        title: 'Dashboards & Analytics',
        color: '#722ed1',
        roles: ['user', 'admin'],
        topics: [
            {
                id: 'db-executive',
                title: 'Executive Dashboard',
                readTime: 5,
                graphic: <DashboardMockup />,
                content: `
The **Executive Dashboard** (Reports → Executive Dashboard) is the primary national-level analytics view. It has 10 sections navigable from a sticky tab bar at the top.

| Section | What it shows |
|---|---|
| Overview | KPI cards: avg score, facilities assessed, high performers, needs attention |
| Trend | 12-month national compliance trend with 90% target and 85% benchmark |
| Schedules | Audit schedule status: completed, in progress, overdue, upcoming |
| Clauses | Radar chart, clause bar chart, heatmap by facility |
| Sub-Standards | Per-clause average score cards |
| Low MEs | Clause results below 70% flagged for attention |
| KPI vs Actuals | Gauge charts showing performance against defined targets |
| Quality Index | Composite QoC + survey weighted index per facility |
| Survey Dashboard | Inpatient/outpatient satisfaction survey results |
| Period Comparison | Multi-cycle comparison (up to 5 periods) |

**The main filter bar** (sticky at top): Set Facility, Level, Clause, Date Range, and Period — all sections update together.
                `
            },
            {
                id: 'db-filters',
                title: 'Using Filters',
                readTime: 2,
                content: `
The sticky filter bar at the top of the Executive Dashboard has 5 filters:

| Filter | Effect |
|---|---|
| Facility | Show data for one specific facility only |
| Level | Filter by facility type (e.g. Hospital, Clinic) |
| Clause | Filter all charts to one clause |
| Date Range | Manual date window override |
| Assessment Period | Snap to a defined period's exact date range |

Selecting an **Assessment Period** automatically adjusts the date range to match that period's start and end dates.

Click **Clear filters ×** to reset all filters back to the national view.

**Tip:** The Period Comparison section inherits your facility, level, and clause filters but uses its own cycle selectors for the date dimension.
                `
            },
            {
                id: 'db-period',
                title: 'Period Comparison',
                readTime: 3,
                content: `
The **Period Comparison** section (Section 10 of the Executive Dashboard) lets you compare up to 5 audit periods side by side.

**How to use:**
1. Scroll to Period Comparison or click "🔄 Period Comparison" in the tab bar
2. Use the Cycle 1–5 selectors to choose which periods to compare
3. Filters from the main bar automatically apply

**What it shows:**
- Overall score cards per period + a Δ change card (last vs first cycle)
- Clause grouped bar chart — all clauses, one bar per period
- Clause trend lines — trajectory across cycles
- Clause table — scores, stars, and delta per period
- Facility delta chart — which facilities improved or regressed
- Facility table — all facility scores across selected periods

**Note:** Requires at least 2 active periods with assessment data configured in Settings → Assessment Periods.
                `
            },
        ]
    },
    {
        id: 'reports',
        icon: '📄',
        title: 'Reports & Exports',
        color: '#eb2f96',
        roles: ['user', 'admin'],
        topics: [
            {
                id: 'rp-facility',
                title: 'Facility Audit Report',
                readTime: 3,
                content: `
The **Facility Report** (Reports → Facility Report) generates a comprehensive audit report for a selected facility.

**To generate:**
1. Select the facility
2. Select the assessment period — look for entries showing "(10 clauses)"
3. The report loads in the preview panel automatically

**Export options:**

| Format | Best for |
|---|---|
| Print / Save PDF | Quick sharing via browser |
| Narrative PDF Report | Formal submissions — full narrative text per clause |
| Download Excel | Data analysis — one sheet per clause |

**Narrative PDF structure:**
- Cover page with facility details, score, and star rating
- Executive summary with narrative paragraph
- Table of contents and abbreviations
- Introduction, scope, objectives, methodology
- Findings per clause: summary table + nonconformity table
- Corrective Action Plan (if CAP items exist)
- Conclusion with improvement recommendations

**Important:** Always select the grouped period entry showing the clause count (e.g. "10 clauses") for the full report rather than a single-clause entry.
                `
            },
            {
                id: 'rp-excel',
                title: 'Excel Export',
                readTime: 2,
                content: `
The Excel export produces a workbook with:
- **Cover Page** sheet: facility info, overall score, and section scores table
- **One sheet per clause** (AACC through ISM) with all ME responses

Each clause sheet has:
- Colour-coded cells (green = Met, orange = Partial, red = Not Met)
- Auto-filter on header row
- Frozen panes to keep the header visible while scrolling
- KCS MEs highlighted in red

The file is named: \`QoC_Assessment_{FacilityName}_{Date}.xlsx\`
                `
            },
            {
                id: 'rp-card-export',
                title: 'Chart & Dashboard Export',
                readTime: 2,
                content: `
Every chart card in the Executive Dashboard has **PNG** and **PDF** export buttons in its top-right corner.

- **PNG** — Captures the card at 2.5× scale as a high-resolution image
- **PDF** — Captures at 2.0× scale on A4 landscape with a dark header stripe

The **Download PDF** button in the dashboard header captures all 10 sections into a single multi-page A4 landscape PDF.

**Tip:** Export buttons are hidden during capture so they don't appear in the output image.
                `
            },
        ]
    },
    {
        id: 'surveys',
        icon: '📝',
        title: 'Customer Surveys',
        color: '#13c2c2',
        roles: ['user', 'admin'],
        topics: [
            {
                id: 'sv-public',
                title: 'How Patients Submit Surveys',
                readTime: 3,
                graphic: <SurveyMockup />,
                content: `
Patients submit satisfaction surveys without logging in using a **QR code** generated from the Facility Coordinates page. Scanning the QR code opens the public survey form on the patient's phone.

**Inpatient survey** — For patients admitted to a ward. Covers: staff attitude, supplies, cleanliness, food, communication, and overall satisfaction.

**Outpatient survey** — For patients seen in outpatient departments. Covers: waiting times, staff attitude, cleanliness, medication received, and overall satisfaction.

**How QR codes work:**
- Each facility has a unique QR code generated from its GPS coordinates
- The system checks that the patient is physically at the facility when submitting
- One submission is allowed per device per survey type per facility per year
- Surveys are automatically linked to the current active period

**Geolocation requirement:** Surveys must be submitted within 100 metres of the facility. If a patient is outside this range, they will see a location error.
                `
            },
            {
                id: 'sv-manage',
                title: 'Viewing Survey Data',
                readTime: 2,
                content: `
Go to **National Oversight → Customer Surveys** to:
- View all submitted surveys in a sortable table
- Filter by facility, period, survey type, and date range
- View overall scores and satisfaction ratings
- See the Survey Analytics Dashboard (collapsible panel at the top)

**Scoring:** Each survey item is scored 0–100. The overall score is the mean of all scored items.

**Satisfaction threshold:** A survey scoring ≥ 70% is considered "satisfied." The Executive Dashboard shows the percentage of surveys meeting this threshold.

**Quality Index integration:** Survey scores are weighted with QoC audit scores (default: 20% survey / 80% QoC) to produce the Quality Index shown in National Oversight → Quality Index.

**Survey Analytics Dashboard:**
The collapsible panel at the top of the Customer Surveys page shows:
- Average Score by Facility — bar charts, Inpatient and Outpatient separately, sorted highest to lowest
- Score by Section — progress bars per survey section
- Satisfaction Distribution — breakdown of satisfaction levels
- Medications Not Received — which medications patients reported not receiving
                `
            },
        ]
    },
        {
            id: 'ays-scorecard',
            title: 'AYS Scorecard',
            icon: '📋',
            color: '#1890ff',
            roles: ['user', 'admin'],
            topics: [
                { id: 'ays-overview', title: 'Overview', readTime: 2, content: `The **AYS (Adolescent and Youth Services) Scorecard** captures patient feedback in enrolled facilities. Peer Educators submit monthly scorecards rating provider behaviour on 21 questions.

**Scale:** Always=4, Mostly=3, Sometimes=2, Never=1
**Threshold:** Mean 3.5+ = Satisfied` },
                { id: 'ays-submit', title: 'Submitting a Scorecard', readTime: 3, roles: ['user'], content: `1. Log in - you land on AYS Scorecard automatically
2. Select month and year
3. Click Open Scorecard
4. Rate each question
5. Save draft at any time
6. Click Submit when all 21 questions are answered` },
                { id: 'ays-admin', title: 'Administration', readTime: 3, roles: ['admin'], content: `Go to **AYS Scorecard - Administration**.

**Questions tab:** Add, edit, deactivate questions.

**Enrolled Facilities tab:** Enrol facilities to allow scorecard submission.` },
                { id: 'ays-analytics', title: 'AYS Analytics', readTime: 2, roles: ['admin'], content: `Go to **AYS Scorecard - AYS Analytics**.

**Questions tab:** National mean score per question.

**By Facility tab:** Each enrolled facility ranked by mean score and satisfaction status.` },
            ],
        },
        {
            id: 'health-awards',
            title: 'Health Excellence Awards',
            icon: '🏆',
            color: '#d97706',
            roles: ['user', 'admin'],
            topics: [
                { id: 'enhea-overview', title: 'ENHEA Overview', readTime: 3, content: `The **Health Excellence Awards (ENHEA)** recognises outstanding health facilities and programmes annually.

**Facility Awards** - QoC assessments for nominated facilities ranked by facility type.
**Programs Awards** - 14-criteria tool (1-5 scale): Best Public Health Program, Best Administration Department.

**Final Score:** 70% NQMP + 30% Judge Average` },
                { id: 'enhea-nominations', title: 'Facility Nominations', readTime: 3, roles: ['admin'], content: `Go to **Health Excellence Awards - Facility Nominations**.

Nominate facilities for the awards cycle. Can be done before or after assessment.

1. Click Nominate Facilities
2. Select award cycle
3. Select facilities
4. Click Nominate

Award category assigned automatically by facility type. Can withdraw and reinstate at any time.` },
                { id: 'enhea-programs', title: 'Programs Assessment', readTime: 5, content: `Go to **Health Excellence Awards - Programs Assessment**.

Score 14 criteria across 4 sections (1-5):
- Strategic Foundations (SF-1 to SF-3)
- Operational Excellence (OE-1 to OE-4)
- Organizational Capacity (OC-1 to OC-3)
- Service Quality and Innovation (SQI-1 to SQI-4)

**Bands:** Best Practice Champion 85%+, Strong Performer 64%+, Developing Performer 43%+, Needs Support` },
                { id: 'enhea-approval', title: 'Approval and Revision', readTime: 3, roles: ['admin'], content: `Go to **Programs Assessment - Approval Queue**.

**Approve:** Finalises the assessment.
**Return:** Sends back to assessor with a mandatory comment explaining what needs correction.` },
                { id: 'enhea-report', title: 'Narrative Report', readTime: 2, content: `Once approved, click **Report** to download a Word document with:
1. Cover page
2. Overall score and band
3. Introduction narrative
4. Assessment findings table
5. Identified gaps (score 2 or below)
6. Recommendations table
7. Conclusion` },
                { id: 'enhea-judge', title: 'Judge Portal', readTime: 4, roles: ['admin'], content: `Go to **Health Excellence Awards - Judge Portal**.

Score facilities on 8 criteria (1-5 stars, total out of 40):
1. Clinical Excellence
2. Patient Safety
3. Innovation
4. Service Efficiency
5. Community Engagement
6. Leadership
7. Documentation
8. Alignment with Ministry Goals

Estimated final score (70% NQMP + 30% judge) updates live.` },
                { id: 'enhea-dashboard', title: 'Awards Dashboard', readTime: 2, content: `Go to **Health Excellence Awards - Awards Dashboard**.

**Facility Awards:** Nominated facilities ranked by QoC score per award category.

**Programs and Units:** Approved programme assessments ranked by score.

**Individual Awards:** Manual process for 2026.` },
            ],
        },
    {
        id: 'troubleshooting',
        icon: '🔧',
        title: 'Troubleshooting',
        color: '#f5222d',
        roles: ['user', 'admin'],
        topics: [
            {
                id: 'tr-dashboard',
                title: 'Dashboard Shows No Data',
                readTime: 2,
                content: `
**Problem:** Charts and KPI cards show 0 or "No data."

**Check these:**

1. **Period filter** — The dashboard defaults to the current period. If no assessments exist within that period's date range, nothing will show. Try clearing the period filter or selecting a different one.

2. **Assessment status** — Only Submitted or Approved assessments appear in dashboards. Drafts are excluded.

3. **Date range** — The date range must overlap with your assessment dates.

4. **Scores not calculated** — If assessments were bulk-imported without recalculating scores, the overall_percentage field may be 0. Contact your administrator to recalculate scores.
                `
            },
            {
                id: 'tr-report',
                title: 'Report Shows "Not Assessed"',
                readTime: 2,
                content: `
**Problem:** The Facility Report shows data for only one clause; others show "Not assessed."

**Cause:** The report was opened from a single-clause assessment entry rather than the grouped period entry.

**Fix:**
1. Open the Facility Report modal
2. In the assessment dropdown, look for an entry showing "(10 clauses)" — e.g. "FY2025/26 — 81.3% · 15 Jul 25 (10 clauses)"
3. Select that grouped entry

If the dropdown only shows individual clause entries, the assessments have different period labels. Ensure all assessments for the same facility and audit cycle share the same period label.
                `
            },
            {
                id: 'tr-permissions',
                title: 'Cannot See a Page',
                readTime: 2,
                content: `
**Problem:** A navigation item is missing or you see "Access denied."

**Steps to resolve:**
1. Contact your administrator
2. They should go to **Settings → Access Control**
3. Click your role card in the Role Permissions tab
4. Check whether the relevant module is enabled
5. If not, enable it and save
6. **Log out and back in** — permission changes only apply on fresh login

**If you're an administrator:**
- Go to Settings → Access Control → Role Permissions
- Click the user's role
- Enable the missing module
- Save and ask the user to re-login
                `
            },
            {
                id: 'tr-import',
                title: 'KoboCollect Import Errors',
                readTime: 2,
                content: `
**"me_id = 0" or missing responses after import:**
The clause was not selected before importing. KoboCollect exports use short ME codes (e.g. \`1.1\`) — you must select the clause first so the system knows to add the prefix (e.g. \`AACC.1.1\`).

**Duplicate entry error:**
The same assessment has already been imported. Check the assessments list before re-importing.

**Wrong scores after import:**
Verify that all MEs in the import file have valid fulfillment values (Met, Partially Met, Not Met, Not Applicable, or Not Checked). Any other value is ignored during scoring.
                `
            },
        ]
    },
];

const SECTIONS_ADMIN = [
    {
        id: 'admin-access',
        icon: '🔐',
        title: 'Access Control',
        color: '#722ed1',
        roles: ['admin'],
        topics: [
            {
                id: 'ac-overview',
                title: 'Managing Role Permissions',
                readTime: 4,
                graphic: <SettingsMockup />,
                content: `
**Settings → Access Control** is where you manage what each role can see and do on the platform.

**Role Permissions tab:**
- Shows all roles as cards, each with a badge showing how many modules are enabled
- Click any role to open the permissions modal
- The modal shows every active module grouped by category
- Toggle each module on or off with a switch
- Use **Enable All** / **Disable All** for quick bulk changes
- Click **Save Permissions** — changes take effect on users' next login

**Important:** Permission changes do not affect currently logged-in sessions. Users must log out and back in.

**How permissions flow:**
1. Module added to the modules table with a unique key
2. Role is given that module via the Permissions modal → saved to roles.app_modules
3. On login, the server sends the user's enabled modules
4. Frontend reads from localStorage via util.getModules()
5. Nav items check modules['module_key'] === 1 to show/hide
6. Backend checkAccess('module_key') enforces it on every API call
                `
            },
            {
                id: 'ac-modules',
                title: 'Module Management',
                readTime: 3,
                content: `
The **Module Management** tab shows the complete modules table — all available system modules.

**Creating a new module:**
1. Click **New Module**
2. Fill in:
   - **Key** — unique lowercase identifier (e.g. \`qoc_reports\`) — cannot be changed after creation
   - **Display Name** — human-readable label shown in the permissions grid
   - **Group** — which category it appears under (Administration, Quality of Care, etc.)
   - **Applies To** — CLIENT (facility users only), ADMIN, or BOTH
   - **National Level Only** — restricts to national-level users
   - **Active** — inactive modules are hidden from the permissions grid but not deleted
3. Save the module
4. Go back to Role Permissions and enable it for the appropriate roles

**Editing modules:**
- Edit display name, group, or visibility
- Built-in modules (id ≤ 221) are protected from deletion
- Custom modules can be deleted if no longer needed

**Best practice roles:**
| Role | Permissions |
|---|---|
| Facility Assessor | qoc_assess |
| Approval Officer | qoc_approve |
| National Analyst | qoc_view |
| QoC Administrator | qoc_admin |
| Facility Manager | qoc_view + manage_facilities |
                `
            },
        ]
    },
    {
        id: 'admin-periods',
        icon: '📅',
        title: 'Assessment Periods',
        color: '#f59e0b',
        roles: ['admin'],
        topics: [
            {
                id: 'per-create',
                title: 'Creating and Managing Periods',
                readTime: 3,
                content: `
Periods define the audit cycle window, for example FY2025/26: 1 April 2025 – 31 March 2026.

Go to **Settings → Assessment Periods** to:
1. Create a new period with a label, start date, and end date
2. Mark one period as **Current** — this becomes the default in all dashboards and the public survey form
3. Mark periods as **Active** to include them in filter dropdowns

**Best practice:**
- Always set exactly one period as Current
- Dashboards automatically default to the current period's date range on first load
- Keep previous periods Active so they remain available in filters and Period Comparison

**Period Comparison:** Once you have 2+ active periods with assessment data, the Executive Dashboard's Period Comparison section can compare performance across up to 5 cycles.
                `
            },
            {
                id: 'per-schedules',
                title: 'Audit Schedules',
                readTime: 3,
                content: `
Audit Schedules define when each facility is planned to be audited within a period.

Go to **Settings → Audit Schedules** to:
1. Create a new schedule by selecting facility, period, and planned date
2. Assign a lead assessor from the user list
3. Track status: Planned → In Progress → Completed / Overdue

**How schedules link to assessments:**
When an assessor creates an assessment, they can link it to an existing schedule. Once an assessment is submitted, the schedule status automatically updates to "Completed."

Overdue schedules (planned date passed, no assessment) appear in the Executive Dashboard's Schedules section and KPI cards.

**Bulk schedule creation:**
Select multiple facilities and a date range to auto-generate one schedule per facility — useful at the start of each audit period.
                `
            },
        ]
    },
    {
        id: 'admin-users',
        icon: '👥',
        title: 'Users & Facilities',
        color: '#60a5fa',
        roles: ['admin'],
        topics: [
            {
                id: 'usr-manage',
                title: 'Managing Users',
                readTime: 3,
                content: `
Go to **Administration → Roles & Users** (requires \`manage_roles_users\` permission).

**Adding a new user:**
1. Click **Add User**
2. Set first name, last name, email, mobile, and username
3. Set a password
4. Assign a **Role** — the role controls which modules the user can access
5. Set scope: National User (no specific facility) or Facility User (assigned to one facility)
6. Set status to Active and save

**User types:**
- **National User** — Can see data across all facilities (assigned to facility ID 137 internally)
- **Facility User** — Sees data for their assigned facility only

**Editing users:**
Click the edit icon on any user row. You can update all fields except username. To change a password, enter a new value in the password field — leave blank to keep the current password.

**Best practice:** Create separate roles per function. Never give all users qoc_admin access.
                `
            },
            {
                id: 'fac-manage',
                title: 'Managing Facilities',
                readTime: 3,
                content: `
Go to **Administration → Facilities** (requires \`manage_facilities\` permission).

**Facility record key fields:**
| Field | Description |
|---|---|
| Business Name | Full official facility name |
| Site Type / Level | e.g. Clinic without Maternity, Regional Hospital |
| Located Region | Hhohho, Manzini, Lubombo, or Shiselweni |
| Facility Code | Unique identifier |
| Status | Active / Inactive |

**Adding facilities from HIE (recommended):**
Use **Settings → HIE Facility Sync** to import directly from the national Health Information Exchange. This ensures alignment with the national registry.

**Adding manually:**
1. Click **Add Facility**
2. Fill in Business Name, Site Type, and Region (all mandatory)
3. Set status to Active and save

**Why accuracy matters:** Facility name, region, and level appear in all exports, dashboards, and dashboard filters. The "Level" dashboard filter uses the Site Type field specifically.
                `
            },
        ]
    },
    {
        id: 'admin-hie',
        icon: '🔄',
        title: 'HIE Facility Sync',
        color: '#0ea5e9',
        roles: ['admin'],
        topics: [
            {
                id: 'hie-setup',
                title: 'Setting Up HIE Sync',
                readTime: 4,
                content: `
**Settings → HIE Facility Sync** connects the platform to the national Health Information Exchange (HIE) using HL7 FHIR R4.

**One-time setup:**
1. Enter the **FHIR Base URL** — e.g. \`https://hie.health.gov.sz/fhir\`
2. Select **Auth Type** — Bearer Token, API Key, or None
3. Paste the **Auth Token** if required
4. Choose **Sync Mode**:
   - *Add new + Update changed* — adds new facilities AND updates existing ones when HIE data has changed
   - *Add new only* — only adds facilities that don't yet exist (never overwrites local data)
5. Click **Save Configuration**
6. Click **Test Connection** — should show green "Connected"

**FHIR field mapping:**
| FHIR field | QoC field |
|---|---|
| Location.id | hie_id (internal link) |
| Location.name | Facility name |
| Location.address.district | Region |
| Location.type | Site type |
| Location.telecom[phone] | Phone |
                `
            },
            {
                id: 'hie-sync',
                title: 'Running a Sync',
                readTime: 3,
                content: `
1. Click **Fetch from HIE & Preview** — downloads all Location resources and shows a diff table:
   - 🟢 **New** — exists in HIE but not in QoC
   - 🔵 **Update** — exists in both but fields have changed
   - **No Change** — already up to date
   - ⚠ **Blank** — HIE didn't populate that field (common for region or type)

2. Review the table — check the Action and Changes columns before proceeding

3. Click **Sync Now** → confirm in the modal → sync applies

**Blank fields:**
If HIE doesn't have region or facility type, the facility is still imported (with name and HIE ID). Edit blank fields manually in Administration → Facilities after sync.

**Facilities are never deleted by sync.** Only additions and updates are applied.

**Login badge:**
If new or changed facilities are detected in HIE since your last login, a red badge appears on the HIE Facility Sync menu item. The badge is cached for 6 hours.
                `
            },
        ]
    },
    {
        id: 'admin-standards',
        icon: '📐',
        title: 'Standards Setup',
        color: '#1890ff',
        roles: ['admin'],
        topics: [
            {
                id: 'std-manage',
                title: 'Managing Standards & MEs',
                readTime: 3,
                content: `
Go to **Quality of Care → Standards Setup** (requires \`qoc_admin\` permission).

The standards hierarchy is:
**Standard → Clause → Sub-Standard → Measurable Element (ME)**

**What you can do:**
- View all clauses, standards, sub-standards, and measurable elements
- Mark specific MEs as **Key Critical Standards (KCS)**
- Update ME text or add new MEs
- Manage the standards hierarchy

**⚠ Warning:** Changes to MEs affect all future assessments. Existing responses are not retroactively changed. Consult the team before modifying active MEs.

**KPI Targets:**
Go to **National Oversight → KPI Targets** to set expected performance levels by clause and period. These appear in the Executive Dashboard's KPI vs Actuals section (Section 7) as gauge charts.
                `
            },
        ]
    },
];

// ─── Render content ───────────────────────────────────────────────────────────
function renderContent(text) {
    const lines = text.trim().split('\n');
    const elements = [];
    let i = 0;
    let tableRows = [];
    let inTable = false;

    const flushTable = () => {
        if (tableRows.length < 2) { tableRows = []; inTable = false; return; }
        const headers = tableRows[0].split('|').map(h => h.trim()).filter(Boolean);
        const rows = tableRows.slice(2).map(r => r.split('|').map(c => c.trim()).filter(Boolean));
        elements.push(
            <div key={`table-${i}`} style={{ overflowX: 'auto', marginBottom: 20 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                    <thead>
                        <tr style={{ background: '#f0f5ff' }}>
                            {headers.map((h, hi) => (
                                <th key={hi} style={{ padding: '10px 14px', textAlign: 'left', borderBottom: '2px solid #d6e4ff', fontWeight: 700, color: '#1a1a2e', fontSize: 14 }}>{h}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, ri) => (
                            <tr key={ri} style={{ background: ri % 2 ? '#fafbff' : '#fff' }}>
                                {row.map((cell, ci) => (
                                    <td key={ci} style={{ padding: '9px 14px', borderBottom: '1px solid #e8f0fe', fontSize: 14 }}>
                                        {renderInline(cell)}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
        tableRows = []; inTable = false;
    };

    while (i < lines.length) {
        const line = lines[i];
        if (line.startsWith('|')) { inTable = true; tableRows.push(line); i++; continue; }
        if (inTable) flushTable();

        if (line.startsWith('**') && line.endsWith('**') && !line.slice(2, -2).includes('**')) {
            elements.push(<div key={i} style={{ fontWeight: 800, fontSize: 16, color: '#0f172a', marginTop: 22, marginBottom: 8 }}>{line.slice(2, -2)}</div>);
        } else if (line.startsWith('```')) {
            const codeLines = [];
            i++;
            while (i < lines.length && !lines[i].startsWith('```')) { codeLines.push(lines[i]); i++; }
            elements.push(
                <pre key={i} style={{ background: '#0f172a', color: '#e2e8f0', padding: '16px 20px', borderRadius: 10, fontSize: 13, overflowX: 'auto', marginBottom: 20, lineHeight: 1.8 }}>
                    <code>{codeLines.join('\n')}</code>
                </pre>
            );
        } else if (line.startsWith('- ')) {
            const items = [line.slice(2)];
            i++;
            while (i < lines.length && lines[i].startsWith('- ')) { items.push(lines[i].slice(2)); i++; }
            elements.push(
                <ul key={i} style={{ paddingLeft: 22, marginBottom: 16 }}>
                    {items.map((item, ii) => <li key={ii} style={{ marginBottom: 6, fontSize: 15, color: '#334155', lineHeight: 1.8 }}>{renderInline(item)}</li>)}
                </ul>
            );
            continue;
        } else if (line.match(/^\d+\. /)) {
            const items = [line.replace(/^\d+\. /, '')];
            i++;
            while (i < lines.length && lines[i].match(/^\d+\. /)) { items.push(lines[i].replace(/^\d+\. /, '')); i++; }
            elements.push(
                <ol key={i} style={{ paddingLeft: 22, marginBottom: 16 }}>
                    {items.map((item, ii) => <li key={ii} style={{ marginBottom: 8, fontSize: 15, color: '#334155', lineHeight: 1.8 }}>{renderInline(item)}</li>)}
                </ol>
            );
            continue;
        } else if (line === '') {
            elements.push(<div key={i} style={{ height: 10 }} />);
        } else {
            elements.push(<p key={i} style={{ fontSize: 15, color: '#334155', lineHeight: 1.9, marginBottom: 12 }}>{renderInline(line)}</p>);
        }
        i++;
    }
    if (inTable) flushTable();
    return elements; 
}

function renderInline(text) {
    const parts = text.split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
    return parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) return <strong key={i} style={{ color: '#0f172a', fontWeight: 700 }}>{part.slice(2, -2)}</strong>;
        if (part.startsWith('`') && part.endsWith('`')) return <code key={i} style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: 4, fontSize: 13, fontFamily: 'monospace', color: '#0369a1' }}>{part.slice(1, -1)}</code>;
        return part;
    });
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function QoCUserManual() {
    const [role, setRole] = useState('user');
    const [activeSection, setActiveSection] = useState('getting-started');
    const [activeTopic, setActiveTopic] = useState('gs-overview');
    const [search, setSearch] = useState('');
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [tourMode, setTourMode]       = useState(false);
    const [tourStep, setTourStep]        = useState(0);
    const [voiceOn, setVoiceOn]          = useState(true);
    const [speaking, setSpeaking]        = useState(false);
    const [completed, setCompleted] = useState({});
    const contentRef = useRef(null);

    const SECTIONS = role === 'admin' ? [...SECTIONS_USER, ...SECTIONS_ADMIN] : SECTIONS_USER;
    const allTopics = SECTIONS.flatMap(s => s.topics.map(t => ({ ...t, sectionId: s.id, sectionTitle: s.title, sectionColor: s.color, sectionIcon: s.icon })));

    const searchResults = search.trim().length > 1
        ? allTopics.filter(t => t.title.toLowerCase().includes(search.toLowerCase()) || t.content.toLowerCase().includes(search.toLowerCase()))
        : [];

    const currentSection = SECTIONS.find(s => s.id === activeSection) || SECTIONS[0];
    const currentTopic = allTopics.find(t => t.id === activeTopic) || allTopics[0];

    const totalTopics = allTopics.length;
    const completedCount = Object.keys(completed).length;
    const progressPct = Math.round((completedCount / totalTopics) * 100);

    const navigate = (sectionId, topicId) => {
        setActiveSection(sectionId);
        setActiveTopic(topicId);
        setSearch('');
        if (contentRef.current) contentRef.current.scrollTop = 0;
    };

    const markDone = (topicId) => {
        setCompleted(prev => ({ ...prev, [topicId]: true }));
    };

    // Auto-set first section when role changes
    useEffect(() => {
        const sections = role === 'admin' ? [...SECTIONS_USER, ...SECTIONS_ADMIN] : SECTIONS_USER;
        setActiveSection(sections[0].id);
        setActiveTopic(sections[0].topics[0].id);
    }, [role]);

    const totalReadTime = allTopics.reduce((a, t) => a + (t.readTime || 3), 0);

    return (
        <div className="page-content" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <div className="page-head uc d-flex" style={{ background: 'linear-gradient(135deg,#0f172a,#1e3a5f)', padding: '14px 24px', flexShrink: 0 }}>
                <div className="my-auto" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <BookOutlined style={{ fontSize: 22, color: '#60a5fa' }} />
                    <div>
                        <h2 style={{ margin: 0, color: '#fff', fontSize: 18, fontWeight: 800 }}>User Manual</h2>
                        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>Eswatini QoC Platform — Self-Paced Learning Guide</div>
                    </div>
                </div>
                <div className="my-auto ml-auto" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    {/* Role toggle */}
                    <div style={{ display: 'flex', background: 'rgba(255,255,255,0.1)', borderRadius: 8, padding: 3, gap: 3 }}>
                        {Object.entries(ROLES).map(([key, r]) => (
                            <div key={key} onClick={() => setRole(key)}
                                style={{
                                    padding: '5px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                                    background: role === key ? '#fff' : 'transparent',
                                    color: role === key ? r.color : 'rgba(255,255,255,0.6)',
                                    transition: 'all 0.15s',
                                }}>
                                {r.icon} {r.label}
                            </div>
                        ))}
                    </div>
                    <Input
                        prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
                        placeholder="Search manual…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        style={{ width: 200, background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff' }}
                        allowClear
                    />
                    <button onClick={() => {
                        const el = document.getElementById('qoc-manual-print-area');
                        if (!el) { window.print(); return; }
                        const win = window.open('', '_blank');
                        win.document.write('<html><head><title>QoC Platform User Manual</title><style>body{font-family:Arial,sans-serif;font-size:13px;line-height:1.6;color:#111;padding:20px;max-width:900px;margin:0 auto}h1,h2,h3{color:#0d2137}@media print{button{display:none}}</style></head><body>');
                        win.document.write('<h1>QoC Platform - User Manual</h1>');
                        win.document.write(el.innerHTML);
                        win.document.write('</body></html>');
                        win.document.close();
                        setTimeout(() => win.print(), 500);
                    }} style={{ cursor:'pointer', background:'rgba(255,255,255,0.12)', border:'1px solid rgba(255,255,255,0.2)', color:'rgba(255,255,255,0.8)', borderRadius:6, padding:'5px 12px', fontSize:12, display:'flex', alignItems:'center', gap:6 }}>
                        🖨️ Print PDF
                    </button>
                    <button onClick={() => { setTourMode(true); setTourStep(0); setTimeout(() => { if(voiceOn && window.speechSynthesis){ const u=new SpeechSynthesisUtterance('Welcome to the QoC Platform guided tour.'); u.lang='en-US'; window.speechSynthesis.speak(u); } }, 300); }} style={{ cursor:'pointer', background:'#1d4ed8', border:'none', color:'#fff', borderRadius:6, padding:'5px 12px', fontSize:12, display:'flex', alignItems:'center', gap:6 }}>
                        ▶ Guided Tour
                    </button>
                    <span onClick={() => setSidebarOpen(v => !v)} style={{ cursor: 'pointer', color: 'rgba(255,255,255,0.6)', fontSize: 18, padding: '4px 8px' }}>
                        {sidebarOpen ? <CloseOutlined /> : <MenuOutlined />}
                    </span>
                </div>
            </div>

            {/* Progress bar */}
            <div style={{ background: '#1e293b', padding: '8px 24px', display: 'flex', alignItems: 'center', gap: 16, flexShrink: 0 }}>
                <div style={{ flex: 1 }}>
                    <Progress percent={progressPct} showInfo={false} strokeColor="#60a5fa" trailColor="rgba(255,255,255,0.1)" size="small" style={{ margin: 0 }} />
                </div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', whiteSpace: 'nowrap' }}>
                    {completedCount} of {totalTopics} topics · ~{totalReadTime} min read
                </div>
            </div>

            {/* Search overlay */}
            {search.trim().length > 1 && (
                <div style={{ position: 'absolute', top: 110, right: 24, zIndex: 999, width: 360, background: '#fff', borderRadius: 10, boxShadow: '0 8px 32px rgba(0,0,0,0.15)', border: '1px solid #e8f0fe', maxHeight: 420, overflowY: 'auto' }}>
                    <div style={{ padding: '10px 16px', borderBottom: '1px solid #f0f0f0', fontSize: 12, color: '#888', fontWeight: 600 }}>
                        {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} for "{search}"
                    </div>
                    {searchResults.length === 0
                        ? <div style={{ padding: 24, textAlign: 'center', color: '#aaa', fontSize: 14 }}>No results found</div>
                        : searchResults.map(t => (
                            <div key={t.id} onClick={() => navigate(t.sectionId, t.id)}
                                style={{ padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #f5f5f5' }}
                                onMouseEnter={e => e.currentTarget.style.background = '#f8faff'}
                                onMouseLeave={e => e.currentTarget.style.background = ''}>
                                <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>{t.title}</div>
                                <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                                    <span style={{ color: t.sectionColor }}>{t.sectionIcon} {t.sectionTitle}</span>
                                    {completed[t.id] && <span style={{ marginLeft: 8, color: '#52c41a' }}>✓ Done</span>}
                                </div>
                            </div>
                        ))
                    }
                </div>
            )}

            {/* Body */}
            <div style={{ flex: 1, display: 'flex', overflow: 'hidden', background: '#f8faff' }}>
                {/* Sidebar */}
                {sidebarOpen && (
                    <div style={{ width: 272, flexShrink: 0, background: '#fff', borderRight: '1px solid #e8f0fe', overflowY: 'auto', padding: '16px 0' }}>
                        {/* Role badge */}
                        <div style={{ margin: '0 16px 16px', padding: '10px 14px', background: `${ROLES[role].color}10`, borderRadius: 8, border: `1px solid ${ROLES[role].color}30` }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: ROLES[role].color }}>{ROLES[role].icon} {ROLES[role].label} Manual</div>
                            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{ROLES[role].desc}</div>
                        </div>
                        {SECTIONS.map(section => (
                            <div key={section.id} style={{ marginBottom: 4 }}>
                                <div onClick={() => navigate(section.id, section.topics[0].id)}
                                    style={{
                                        padding: '9px 20px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                                        background: activeSection === section.id ? `${section.color}12` : 'transparent',
                                        borderLeft: activeSection === section.id ? `3px solid ${section.color}` : '3px solid transparent',
                                        transition: 'all 0.15s',
                                    }}
                                    onMouseEnter={e => { if (activeSection !== section.id) e.currentTarget.style.background = '#f8faff'; }}
                                    onMouseLeave={e => { if (activeSection !== section.id) e.currentTarget.style.background = 'transparent'; }}>
                                    <span style={{ fontSize: 16 }}>{section.icon}</span>
                                    <span style={{ fontSize: 13, fontWeight: activeSection === section.id ? 700 : 500, color: activeSection === section.id ? section.color : '#475569', flex: 1 }}>
                                        {section.title}
                                    </span>
                                    {section.roles?.includes('admin') && !section.roles?.includes('user') && (
                                        <Tag style={{ fontSize: 9, padding: '0 4px', border: 'none', background: '#722ed115', color: '#722ed1' }}>Admin</Tag>
                                    )}
                                    <Badge count={section.topics.filter(t => completed[t.id]).length} style={{ background: '#52c41a', fontSize: 10, height: 16, minWidth: 16, lineHeight: '16px' }}
                                        showZero={false} />
                                </div>
                                {activeSection === section.id && (
                                    <div style={{ paddingLeft: 8, marginTop: 2 }}>
                                        {section.topics.map(topic => (
                                            <div key={topic.id}
                                                onClick={() => { setActiveTopic(topic.id); if (contentRef.current) contentRef.current.scrollTop = 0; }}
                                                style={{
                                                    padding: '7px 20px 7px 28px', cursor: 'pointer', fontSize: 13,
                                                    color: activeTopic === topic.id ? section.color : '#64748b',
                                                    fontWeight: activeTopic === topic.id ? 700 : 400,
                                                    background: activeTopic === topic.id ? `${section.color}10` : 'transparent',
                                                    borderRadius: 4,
                                                    borderLeft: activeTopic === topic.id ? `2px solid ${section.color}` : '2px solid transparent',
                                                    transition: 'all 0.1s', display: 'flex', alignItems: 'center', gap: 6,
                                                }}
                                                onMouseEnter={e => { if (activeTopic !== topic.id) e.currentTarget.style.background = '#f1f5f9'; }}
                                                onMouseLeave={e => { if (activeTopic !== topic.id) e.currentTarget.style.background = 'transparent'; }}>
                                                {completed[topic.id]
                                                    ? <CheckCircleFilled style={{ fontSize: 12, color: '#52c41a', flexShrink: 0 }} />
                                                    : <span style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: activeTopic === topic.id ? section.color : '#cbd5e1', display: 'inline-block' }} />
                                                }
                                                <span style={{ flex: 1 }}>{topic.title}</span>
                                                {topic.readTime && <span style={{ fontSize: 10, color: '#94a3b8' }}>{topic.readTime}m</span>}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                        <div style={{ margin: '24px 16px 8px', padding: '12px', background: '#f8faff', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                            <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, marginBottom: 2 }}>Eswatini Ministry of Health</div>
                            <div style={{ fontSize: 11, color: '#cbd5e1' }}>National Quality Management Programme</div>
                        </div>
                    </div>
                )}

                {/* Content */}
                <div ref={contentRef} style={{ flex: 1, overflowY: 'auto', padding: '36px 48px' }}>
                    {currentTopic && currentSection && (
                        <>
                            {/* Breadcrumb */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 24, fontSize: 13, color: '#94a3b8' }}>
                                <span style={{ color: currentSection.color, fontWeight: 600 }}>{currentSection.icon} {currentSection.title}</span>
                                <span>›</span>
                                <span style={{ color: '#475569' }}>{currentTopic.title}</span>
                                {currentTopic.readTime && (
                                    <span style={{ marginLeft: 'auto', background: '#f0f5ff', padding: '2px 10px', borderRadius: 20, fontSize: 12, color: '#1890ff' }}>
                                        ~{currentTopic.readTime} min read
                                    </span>
                                )}
                            </div>

                            {/* Title */}
                            <div style={{ marginBottom: 28, paddingBottom: 20, borderBottom: `2px solid ${currentSection.color}20` }}>
                                <h1 style={{ margin: 0, fontSize: 28, fontWeight: 900, color: '#0f172a', lineHeight: 1.3 }}>{currentTopic.title}</h1>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
                                    <Tag style={{ background: `${currentSection.color}15`, color: currentSection.color, border: 'none', fontWeight: 600, fontSize: 12 }}>
                                        {currentSection.icon} {currentSection.title}
                                    </Tag>
                                    {completed[currentTopic.id] && (
                                        <Tag style={{ background: '#f6ffed', color: '#52c41a', border: '1px solid #b7eb8f', fontSize: 12 }}>
                                            <CheckCircleFilled style={{ marginRight: 4 }} />Completed
                                        </Tag>
                                    )}
                                </div>
                            </div>

                            {/* Graphic */}
                            {currentTopic.graphic && (
                                <div style={{ marginBottom: 28 }}>
                                    {currentTopic.graphic}
                                    <div style={{ textAlign: 'center', fontSize: 12, color: '#94a3b8', marginTop: 6 }}>
                                        UI illustration — {currentTopic.title}
                                    </div>
                                </div>
                            )}

                            {/* Content */}
                            <div style={{ maxWidth: 800 }}>
                                {renderContent(currentTopic.content)}
                            </div>

                            {/* Mark complete button */}
                            {!completed[currentTopic.id] && (
                                <div style={{ marginTop: 32, padding: '16px 20px', background: '#f0f9ff', borderRadius: 10, border: '1px solid #bae6fd', display: 'flex', alignItems: 'center', gap: 16, maxWidth: 800 }}>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Done reading?</div>
                                        <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>Mark this topic as complete to track your progress</div>
                                    </div>
                                    <div onClick={() => markDone(currentTopic.id)}
                                        style={{ cursor: 'pointer', background: '#0ea5e9', color: '#fff', padding: '8px 20px', borderRadius: 8, fontSize: 13, fontWeight: 700, whiteSpace: 'nowrap' }}>
                                        ✓ Mark Complete
                                    </div>
                                </div>
                            )}

                            {/* Prev / Next */}
                            <div style={{ marginTop: 40, paddingTop: 20, borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', gap: 12, maxWidth: 800 }}>
                                {(() => {
                                    const flat = SECTIONS.flatMap(s => s.topics.map(t => ({ ...t, sectionId: s.id, sectionColor: s.color })));
                                    const idx = flat.findIndex(t => t.id === activeTopic);
                                    const prev = flat[idx - 1];
                                    const next = flat[idx + 1];
                                    return (
                                        <>
                                            {prev
                                                ? <div onClick={() => navigate(prev.sectionId, prev.id)}
                                                    style={{ cursor: 'pointer', padding: '12px 18px', borderRadius: 10, border: '1px solid #e2e8f0', background: '#fff', maxWidth: '45%' }}
                                                    onMouseEnter={e => e.currentTarget.style.borderColor = prev.sectionColor}
                                                    onMouseLeave={e => e.currentTarget.style.borderColor = '#e2e8f0'}>
                                                    <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 3 }}>← Previous</div>
                                                    <div style={{ fontSize: 14, fontWeight: 600, color: '#334155' }}>{prev.title}</div>
                                                </div>
                                                : <div />
                                            }
                                            {next
                                                ? <div onClick={() => {
                                                    if (!completed[currentTopic.id]) markDone(currentTopic.id);
                                                    navigate(next.sectionId, next.id);
                                                }}
                                                    style={{ cursor: 'pointer', padding: '12px 18px', borderRadius: 10, border: `1px solid ${next.sectionColor || '#e2e8f0'}`, background: '#fff', maxWidth: '45%', textAlign: 'right' }}
                                                    onMouseEnter={e => e.currentTarget.style.background = '#f8faff'}
                                                    onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                                                    <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 3 }}>Next →</div>
                                                    <div style={{ fontSize: 14, fontWeight: 600, color: '#334155' }}>{next.title}</div>
                                                </div>
                                                : <div style={{ padding: '12px 18px', borderRadius: 10, background: '#f6ffed', border: '1px solid #b7eb8f', textAlign: 'center' }}>
                                                    <div style={{ fontSize: 16 }}>🎉</div>
                                                    <div style={{ fontSize: 13, fontWeight: 700, color: '#52c41a' }}>You've completed the manual!</div>
                                                </div>
                                            }
                                        </>
                                    );
                                })()}
                            </div>
                        </>
                    )}
                </div>
            </div>
            {/* Guided Tour */}
            {tourMode && (() => {
                const steps = [
                    { t: 'Welcome', b: 'This guided tour walks you through the main features. Use Next and Back to navigate.' },
                    { t: 'Navigation', b: 'The left sidebar organises all modules. Your role controls what you see.' },
                    { t: 'Quality Assessments', b: 'Create facility assessments, score measurable elements, and submit for approval.' },
                    { t: 'Approval Workflow', b: 'Submitted assessments appear in the Approval Queue for review and approval.' },
                    { t: 'Executive Dashboard', b: 'National performance at a glance - scores, trends, heatmaps, and KPIs.' },
                    { t: 'Health Excellence Awards', b: 'Nominate facilities, assess programmes, coordinate judges, view rankings.' },
                    { t: 'AYS Scorecard', b: 'Peer Educators submit monthly patient feedback scorecards.' },
                    { t: 'End of Tour', b: 'You have completed the tour. Browse the manual sections for detailed guidance.' },
                ];
                const s = steps[Math.min(tourStep, steps.length-1)];
                const last = tourStep >= steps.length-1;
                const go = (i) => {
                    window.speechSynthesis?.cancel();
                    setTourStep(i);
                    if (voiceOn && window.speechSynthesis) {
                        setTimeout(() => {
                            const u = new SpeechSynthesisUtterance(steps[i].t + '. ' + steps[i].b);
                            u.lang='en-US'; u.rate=0.95;
                            const vs = window.speechSynthesis.getVoices();
                            const v = vs.find(v=>v.lang==='en-US')||vs[0];
                            if(v) u.voice=v;
                            u.onstart=()=>setSpeaking(true); u.onend=()=>setSpeaking(false);
                            window.speechSynthesis.speak(u);
                        }, 150);
                    }
                };
                return (
                    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.75)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center'}}
                        onClick={()=>{window.speechSynthesis?.cancel();setTourMode(false);}}>
                        <div onClick={e=>e.stopPropagation()} style={{background:'#fff',borderRadius:16,padding:32,maxWidth:480,width:'92%',boxShadow:'0 24px 64px rgba(0,0,0,0.3)'}}>
                            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
                                <div style={{display:'flex',alignItems:'center',gap:8}}>
                                    <span style={{fontSize:11,color:'#888',fontWeight:600}}>Step {tourStep+1} of {steps.length}</span>
                                    {speaking && <span style={{fontSize:10,color:'#1d4ed8',fontWeight:700}}>● Speaking</span>}
                                </div>
                                <div style={{display:'flex',gap:8}}>
                                    <button onClick={()=>{setVoiceOn(v=>{if(v)window.speechSynthesis?.cancel();return !v;});}}
                                        style={{background:voiceOn?'#eff6ff':'#f3f4f6',border:'1px solid '+(voiceOn?'#bfdbfe':'#e5e7eb'),borderRadius:6,padding:'4px 10px',cursor:'pointer',fontSize:12,color:voiceOn?'#1d4ed8':'#9ca3af'}}>
                                        {voiceOn ? '🔊 Voice On' : '🔇 Voice Off'}
                                    </button>
                                    <button onClick={()=>{window.speechSynthesis?.cancel();setTourMode(false);}} style={{background:'none',border:'none',cursor:'pointer',fontSize:20,color:'#9ca3af'}}>✕</button>
                                </div>
                            </div>
                            <div style={{fontSize:11,color:'#1d4ed8',fontWeight:700,textTransform:'uppercase',letterSpacing:1,marginBottom:6}}>Guided Tour</div>
                            <h2 style={{margin:'0 0 12px',fontSize:22,color:'#0d2137'}}>{s.t}</h2>
                            <p style={{color:'#4b5563',lineHeight:1.7,marginBottom:24,fontSize:14}}>{s.b}</p>
                            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
                                {tourStep>0 && <button onClick={()=>go(tourStep-1)} style={{padding:'9px 20px',borderRadius:8,border:'1px solid #e5e7eb',background:'#fff',cursor:'pointer',fontWeight:600}}>Back</button>}
                                <button onClick={()=>{if(last){window.speechSynthesis?.cancel();setTourMode(false);}else{go(tourStep+1);}}}
                                    style={{padding:'9px 24px',borderRadius:8,border:'none',background:'#1d4ed8',color:'#fff',cursor:'pointer',fontWeight:700,fontSize:14}}>
                                    {last ? 'Finish' : 'Next'}
                                </button>
                            </div>
                            <div style={{display:'flex',gap:6,justifyContent:'center',marginTop:20}}>
                                {steps.map((_,i)=>(
                                    <div key={i} onClick={()=>go(i)} style={{width:i===tourStep?20:8,height:8,borderRadius:4,cursor:'pointer',transition:'all 0.2s',background:i===tourStep?'#1d4ed8':'#e5e7eb'}} />
                                ))}
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
}